import React from "react";
import { useSignOnTracker } from "./hooks/useSignOnTracker";

interface Props {
  username?: string;
  display: { SignOnTracker: boolean };
}

const TARGET_HOURS = 10;

const cardBase =
  "bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md p-4";

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

const getDayOfWeek = (date: string) =>
  new Date(date).toLocaleDateString("en-US", { weekday: "long" });

const SignOnTrackerCard: React.FC<Props> = ({ username, display }) => {
  const enabled = !!username && display.SignOnTracker;
  const { data, loading } = useSignOnTracker(username, enabled);

  if (!display.SignOnTracker) return null;

  if (loading) {
    return (
      <div className="col-span-12">
        <div className={cardBase}>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Sign On Bonus Tracker</h3>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading Sign On Tracker...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;
  
  const pct = Math.min((data.hours / TARGET_HOURS) * 100, 100);
  const isComplete = data.hours >= TARGET_HOURS;

  return (
    <div className="col-span-12">
      <div className={cardBase}>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Sign On Bonus Tracker
        </h3>

        <p className="text-gray-800 dark:text-gray-200 mb-3">
          Total Hours Completed Towards Goal:{" "}
          <strong>{data.hours}</strong>
        </p>

        {/* Progress bar with 0 / 10 labels */}
        <div className="mb-1 flex justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>0</span>
          <span>Target Hours: {TARGET_HOURS}</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 h-4 rounded overflow-hidden">
          <div
            className={`h-4 rounded transition-all duration-300 ${isComplete ? "bg-green-500" : "bg-yellow-400"}`}
            style={{ width: `${pct}%` }}
          />
        </div>

        {data.scheduledPaymentDate && (
          <p className="mt-3 text-gray-800 dark:text-gray-200">
            Congratulations on earning your Sign-On bonus! Your{" "}
            <strong>${data.amount.toLocaleString()}</strong> payment is scheduled for{" "}
            <strong>
              {getDayOfWeek(data.scheduledPaymentDate)},{" "}
              {formatDate(data.scheduledPaymentDate)}
            </strong>
          </p>
        )}
      </div>
    </div>
  );
};

export default SignOnTrackerCard;
